import { OfferModel } from '../../model/OfferModel';

type OfferHistorySection = 'state' | 'coreData' | 'commercial' | 'products';
export type OfferHistoryCoreField =
    | 'portId'
    | 'agentId'
    | 'dateDelivery'
    | 'buyerReference'
    | 'vendorReference'
    | 'supplyMode'
    | 'noticeDays'
    | 'paymentTerms';
export type OfferHistoryCommercialField = 'volume' | 'ppl' | 'total';
type OfferHistoryProductKind = 'item' | 'surcharge' | 'samplekit';
type OfferHistoryProductChangeType = 'added' | 'removed' | 'changed';

interface OfferHistoryBaseProductChange<T, K extends OfferHistoryProductKind> {
    kind: K;
    changeType: OfferHistoryProductChangeType;
    matchKey: string;
    before?: T;
    after?: T;
    beforeIndex?: number;
    afterIndex?: number;
}

interface OfferHistoryItemChange extends OfferHistoryBaseProductChange<OfferModel['items'][number], 'item'> {}

interface OfferHistorySurchargeChange
    extends OfferHistoryBaseProductChange<OfferModel['surcharges'][number], 'surcharge'> {}

interface OfferHistorySamplekitChange
    extends OfferHistoryBaseProductChange<OfferModel['samplekits'][number], 'samplekit'> {}

export type OfferHistoryProductChange =
    | OfferHistoryItemChange
    | OfferHistorySurchargeChange
    | OfferHistorySamplekitChange;

export interface OfferHistoryTransition {
    id: string;
    before: OfferModel;
    after: OfferModel;
    timestamp: string;
    actorName?: string;
    changedSections: OfferHistorySection[];
    coreDataFields: OfferHistoryCoreField[];
    commercialFields: OfferHistoryCommercialField[];
    productChanges: OfferHistoryProductChange[];
}

const CORE_DATA_FIELDS: OfferHistoryCoreField[] = [
    'portId',
    'agentId',
    'dateDelivery',
    'buyerReference',
    'vendorReference',
    'supplyMode',
    'noticeDays',
    'paymentTerms',
];

const COMMERCIAL_FIELDS: OfferHistoryCommercialField[] = ['volume', 'ppl', 'total'];

const getOfferTimestamp = (offer: OfferModel) => {
    return offer.dateUpdated ?? offer.dateCreated ?? '';
};

const sortByOfferTimestamp = (left: OfferModel, right: OfferModel) => {
    const leftTimestamp = new Date(getOfferTimestamp(left)).getTime();
    const rightTimestamp = new Date(getOfferTimestamp(right)).getTime();

    return leftTimestamp - rightTimestamp;
};

const getActorName = (offer: OfferModel) => {
    const updatedByUserName = [offer.updatedByUser?.firstname, offer.updatedByUser?.lastname].filter(Boolean).join(' ');
    if (updatedByUserName) {
        return updatedByUserName;
    }

    const createdByUserName = [offer.createdByUser?.firstname, offer.createdByUser?.lastname].filter(Boolean).join(' ');
    if (createdByUserName) {
        return createdByUserName;
    }

    return offer.updatedBy ?? offer.createdBy;
};

const moneyEquals = (
    left?: {
        value?: number;
        currency?: string;
    },
    right?: {
        value?: number;
        currency?: string;
    }
) => {
    return left?.value === right?.value && left?.currency === right?.currency;
};

const hasCoreFieldChanged = (field: OfferHistoryCoreField, before: OfferModel, after: OfferModel) => {
    switch (field) {
        case 'portId':
            return before.portId !== after.portId;
        case 'agentId':
            return before.agentId !== after.agentId;
        case 'dateDelivery':
            return before.dateDelivery !== after.dateDelivery;
        case 'buyerReference':
            return before.buyerReference !== after.buyerReference;
        case 'vendorReference':
            return before.vendorReference !== after.vendorReference;
        case 'supplyMode':
            return before.supplyMode !== after.supplyMode;
        case 'noticeDays':
            return before.noticeDays !== after.noticeDays;
        case 'paymentTerms':
            return (
                before.paymentTermReference !== after.paymentTermReference ||
                before.paymentTermReferenceDays !== after.paymentTermReferenceDays
            );
    }
};

const hasCommercialFieldChanged = (field: OfferHistoryCommercialField, before: OfferModel, after: OfferModel) => {
    switch (field) {
        case 'volume':
            return before.volume !== after.volume;
        case 'ppl':
            return !moneyEquals(before.ppl, after.ppl);
        case 'total':
            return !moneyEquals(before.total, after.total);
    }
};

const itemEquals = (before?: OfferModel['items'][number], after?: OfferModel['items'][number]) => {
    if (!before || !after) {
        return false;
    }

    return (
        before.productId === after.productId &&
        before.packType === after.packType &&
        before.unitSize === after.unitSize &&
        before.units === after.units &&
        before.unit === after.unit &&
        before.deleted === after.deleted &&
        moneyEquals(before.price, after.price)
    );
};

const surchargeEquals = (before?: OfferModel['surcharges'][number], after?: OfferModel['surcharges'][number]) => {
    if (!before || !after) {
        return false;
    }

    return (
        before.name === after.name &&
        before.deleted === after.deleted &&
        before.surchargeType === after.surchargeType &&
        moneyEquals(before.value, after.value)
    );
};

const samplekitEquals = (before?: OfferModel['samplekits'][number], after?: OfferModel['samplekits'][number]) => {
    if (!before || !after) {
        return false;
    }

    return (
        before.name === after.name &&
        before.quantity === after.quantity &&
        before.deleted === after.deleted &&
        moneyEquals(before.value, after.value)
    );
};

const withOccurrenceSuffix = <T>(items: T[], getBaseKey: (item: T, index: number) => string) => {
    const occurrences = new Map<string, number>();

    return items.map((item, index) => {
        const baseKey = getBaseKey(item, index);
        const occurrence = occurrences.get(baseKey) ?? 0;

        occurrences.set(baseKey, occurrence + 1);

        return `${baseKey}::${occurrence}`;
    });
};

const buildCollectionChanges = <T, K extends OfferHistoryProductKind>({
    beforeItems,
    afterItems,
    kind,
    getBaseKey,
    equals,
}: {
    beforeItems: T[];
    afterItems: T[];
    kind: K;
    getBaseKey: (item: T, index: number) => string;
    equals: (before?: T, after?: T) => boolean;
}): OfferHistoryBaseProductChange<T, K>[] => {
    const beforeKeys = withOccurrenceSuffix(beforeItems, getBaseKey);
    const afterKeys = withOccurrenceSuffix(afterItems, getBaseKey);
    const beforeMap = new Map<string, { item: T; index: number }>();

    beforeItems.forEach((item, index) => {
        beforeMap.set(beforeKeys[index], { item, index });
    });

    const changes: OfferHistoryBaseProductChange<T, K>[] = [];

    afterItems.forEach((item, index) => {
        const key = afterKeys[index];
        const beforeEntry = beforeMap.get(key);

        if (!beforeEntry) {
            changes.push({
                kind,
                changeType: 'added',
                matchKey: key,
                after: item,
                afterIndex: index,
            });
            return;
        }

        beforeMap.delete(key);

        if (!equals(beforeEntry.item, item)) {
            changes.push({
                kind,
                changeType: 'changed',
                matchKey: key,
                before: beforeEntry.item,
                after: item,
                beforeIndex: beforeEntry.index,
                afterIndex: index,
            });
        }
    });

    beforeMap.forEach(({ item, index }, key) => {
        changes.push({
            kind,
            changeType: 'removed',
            matchKey: key,
            before: item,
            beforeIndex: index,
        });
    });

    return changes;
};

const getSamplekitBaseKey = (samplekit: OfferModel['samplekits'][number]) => {
    return [
        samplekit.name ?? '',
        samplekit.quantity ?? '',
        samplekit.value?.currency ?? '',
        samplekit.value?.value ?? '',
        samplekit.deleted ? 'deleted' : 'active',
    ].join('|');
};

const buildProductChanges = (before: OfferModel, after: OfferModel): OfferHistoryProductChange[] => {
    return [
        ...buildCollectionChanges({
            beforeItems: before.items,
            afterItems: after.items,
            kind: 'item',
            getBaseKey: (item, index) => String(item.index ?? index),
            equals: itemEquals,
        }),
        ...buildCollectionChanges({
            beforeItems: before.surcharges,
            afterItems: after.surcharges,
            kind: 'surcharge',
            getBaseKey: (surcharge, index) => String(surcharge.index ?? index),
            equals: surchargeEquals,
        }),
        ...buildCollectionChanges({
            beforeItems: before.samplekits,
            afterItems: after.samplekits,
            kind: 'samplekit',
            getBaseKey: (samplekit) => getSamplekitBaseKey(samplekit),
            equals: samplekitEquals,
        }),
    ];
};

export const buildOfferHistoryTransitions = (historySnapshots: OfferModel[], currentOffer: OfferModel) => {
    if (!historySnapshots.length) {
        return [];
    }

    const orderedStates = [...historySnapshots].sort(sortByOfferTimestamp).concat(currentOffer);
    const transitions: OfferHistoryTransition[] = [];

    for (let index = 0; index < orderedStates.length - 1; index += 1) {
        const before = orderedStates[index];
        const after = orderedStates[index + 1];

        const stateChanged = before.state !== after.state;
        const coreDataFields = CORE_DATA_FIELDS.filter((field) => hasCoreFieldChanged(field, before, after));
        const commercialFields = COMMERCIAL_FIELDS.filter((field) => hasCommercialFieldChanged(field, before, after));
        const productChanges = buildProductChanges(before, after);

        const changedSections: OfferHistorySection[] = [];

        if (stateChanged) {
            changedSections.push('state');
        }
        if (coreDataFields.length) {
            changedSections.push('coreData');
        }
        if (commercialFields.length) {
            changedSections.push('commercial');
        }
        if (productChanges.length) {
            changedSections.push('products');
        }

        transitions.push({
            id: `${before.id}-${after.id}-${index}`,
            before,
            after,
            timestamp: getOfferTimestamp(after),
            actorName: getActorName(after),
            changedSections,
            coreDataFields,
            commercialFields,
            productChanges,
        });
    }

    return transitions.reverse();
};
